/**
 * Nested saved-view TABLE rendering (#1144), styled per mockup bill-table-1a:
 *
 *   [toolbar: Tracked/All toggle · counts · Expand/Collapse all]
 *   [purple header: bill columns — shown once]
 *   [secondary band: ↳ Workspace | Position | custom fields | Notes — once]
 *   bill row (twisty · number+title · chips · sponsors +N · status pill)
 *     indented workspace rows (avatar · name · position pill · values)
 *
 * No repeated per-bill bands of either kind (QA rounds 4–5): the two header
 * rows at the top label both levels. Composed from GroupedTable primitives;
 * AG Grid still renders every flat configuration. The card counterpart is
 * NestedBillCards (mockup 1c); cell rendering is shared via nestedBillCells.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GroupedTableContainer,
  GroupedTableHeader,
  GroupedTableHeaderCell,
  GroupedTableChildHeader,
  ChildRowMarker,
} from '../GroupedTable';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import type { Bill } from '../../types/bill';
import type { SavedViewColumnEntry, BillWorkspaceRecord } from '../../types/savedViews';
import { BILL_COLUMN_CATALOG, BILL_ACTIONS } from './billColumnCatalog';
import ActionsHeaderHelp from '../ActionsHeaderHelp';
import { subRecordGridTemplate } from './billSubRecordRows';
import {
  billValueCell,
  workspaceValueCell,
  workspaceColumnLabel,
  workspaceColumnDescription,
  WorkspaceName,
  NestedViewToolbar,
  type WorkspaceColumnLabels,
  type RecordEditing,
  type BillActionsConfig,
} from './nestedBillCells';
import { getBillLinkClasses } from '../../utils/linkStyles';
import { useProgressiveRender } from '../../hooks/useProgressiveRender';

export interface NestedBillTableProps {
  bills: Bill[];
  billColumns: SavedViewColumnEntry[];
  workspaceColumns: SavedViewColumnEntry[];
  labels: WorkspaceColumnLabels;
  collapsedBillIds: ReadonlySet<string>;
  onToggleBill: (billId: string) => void;
  onExpandAll: () => void;
  onCollapseAll: () => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /** Wired to the page's existing server-side sort. */
  onSort?: (field: string, direction: 'asc' | 'desc') => void;
  onOpenNotes?: (bill: Bill, record: BillWorkspaceRecord) => void;
  /** In-place editing of workspace cells (Legislation); read-only when absent. */
  editing?: RecordEditing;
  /** Bill-level Actions column config (when the view selects it). */
  billActions?: BillActionsConfig;
  /** Tracked/All toggle; no handler on the Bill Tracking tab (all tracked by definition). */
  trackedMode?: boolean;
  onTrackedModeChange?: (tracked: boolean) => void;
}

const CATALOG_LABELS = new Map(BILL_COLUMN_CATALOG.map((c) => [c.colId, c.label]));

/* Workspace rows indent this far; the top band's first track absorbs the same
   offset so both levels' columns line up exactly. */
const WS_INDENT_PX = 38;
const WS_NAME_PX = 220;

const NestedBillTable: React.FC<NestedBillTableProps> = ({
  bills,
  billColumns,
  workspaceColumns,
  labels,
  collapsedBillIds,
  onToggleBill,
  onExpandAll,
  onCollapseAll,
  sortBy,
  sortDirection,
  onSort,
  onOpenNotes,
  editing,
  billActions,
  trackedMode,
  onTrackedModeChange,
}) => {
  const navigate = useNavigate();

  /*
    Horizontal-scroll affordance. The scroller's native scrollbar renders at
    the BOTTOM of the whole ledger — hundreds of rows down — so zoomed-in
    users saw clipped columns and "no way to scroll right" (QA). Same chevron
    overlay as the flat AG grid, reusing its .ag-scroll-chevron styles.
  */
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const scrollAnimRef = useRef<number | null>(null);

  const updateScrollState = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 2);
  }, []);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    updateScrollState();
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      ro.disconnect();
    };
  }, [updateScrollState, bills]);

  const startScrolling = useCallback((direction: 'left' | 'right') => {
    const step = direction === 'left' ? -8 : 8;
    const animate = () => {
      const el = scrollerRef.current;
      if (!el) return;
      el.scrollLeft += step;
      scrollAnimRef.current = requestAnimationFrame(animate);
    };
    scrollAnimRef.current = requestAnimationFrame(animate);
  }, []);

  const stopScrolling = useCallback(() => {
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
    scrollAnimRef.current = null;
    updateScrollState();
  }, [updateScrollState]);

  useEffect(() => () => {
    if (scrollAnimRef.current) cancelAnimationFrame(scrollAnimRef.current);
  }, []);

  const visibleBillCols = billColumns.filter((c) => !c.hide);
  const visibleWsCols = workspaceColumns.filter((c) => !c.hide);

  const billTemplate = `minmax(240px, 1.6fr) ${subRecordGridTemplate(visibleBillCols)}`;
  const wsRowTemplate = `${WS_NAME_PX}px ${subRecordGridTemplate(visibleWsCols)}`;
  // First track absorbs the row indent so the band lines up with the rows.
  const wsBandTemplate = `${WS_INDENT_PX + WS_NAME_PX}px ${subRecordGridTemplate(visibleWsCols)}`;

  /*
    The container clips overflow (rounded corners), so wide configurations —
    many columns, or browser zoom — must scroll HORIZONTALLY inside it rather
    than being cut off (design rule: tables never clip). The scroller wraps
    both header levels and the body so they stay aligned; min-width is the
    sum of the fixed tracks, letting the flexible tracks grow when there is
    room and the scrollbar appear when there is not.
  */
  const GAP = 14; // gap-3.5
  const PADDING = 40; // px-5 both sides
  const billMinWidth =
    PADDING + 240 + visibleBillCols.reduce((n, c) => n + (c.width ?? 160), 0) + GAP * visibleBillCols.length;
  const wsMinWidth =
    PADDING + WS_INDENT_PX + WS_NAME_PX + visibleWsCols.reduce((n, c) => n + (c.width ?? 160), 0) + GAP * visibleWsCols.length;
  const scrollMinWidth = Math.max(billMinWidth, wsMinWidth);


  /*
    Progressive rendering (M5): mount a window of bills, grow near the bottom.
    Reset when the bill list identity changes (new fetch, sort, filter).
  */
  const { visibleCount, sentinelRef, hasMore } = useProgressiveRender(bills.length, bills);

  const sort = onSort
    ? {
        sortBy: sortBy ?? '',
        sortDir: (sortDirection ?? 'desc') as 'asc' | 'desc',
        onSort: (key: string, defaultDir: 'asc' | 'desc') => {
          const next = sortBy === key ? (sortDirection === 'asc' ? 'desc' : 'asc') : defaultDir;
          onSort(key, next);
        },
      }
    : undefined;

  return (
    <GroupedTableContainer>
      <NestedViewToolbar
        trackedMode={trackedMode}
        onTrackedModeChange={onTrackedModeChange}
        onExpandAll={onExpandAll}
        onCollapseAll={onCollapseAll}
      />

      <div className="relative">
      <div className="overflow-x-auto" ref={scrollerRef}>
      <div style={{ minWidth: scrollMinWidth }}>
      {/* Level-1 header: bill columns, shown once ("Sortable by Bill Number"). */}
      <GroupedTableHeader grid="">
        <div className="grid gap-3.5 w-full" style={{ gridTemplateColumns: billTemplate }}>
          <GroupedTableHeaderCell label="Bill" sortKey="number" defaultDir="asc" sort={sort} />
          {visibleBillCols.map((col) =>
            col.colId === 'actions' && billActions && billActions.keys.length > 0 ? (
              // The legend lists only the actions this view enabled AND this
              // consumer wired a handler for — BillActionsCell skips bookmark /
              // vote-prediction buttons without their handlers (BillTrackingTab
              // passes no onBookmark), and the legend must never document a
              // button the column doesn't render.
              <span key={col.colId} className="inline-flex items-center gap-1.5">
                {CATALOG_LABELS.get(col.colId) ?? col.colId}
                <ActionsHeaderHelp
                  icons={BILL_ACTIONS.filter(
                    (a) =>
                      billActions.keys.includes(a.key) &&
                      (a.key === 'bookmark' ? !!billActions.onBookmark : true) &&
                      (a.key === 'votePredictions' ? !!billActions.onVotePredictions : true)
                  ).map((a) => a.glossaryKey)}
                />
              </span>
            ) : (
              <GroupedTableHeaderCell key={col.colId} label={CATALOG_LABELS.get(col.colId) ?? col.colId} />
            )
          )}
        </div>
      </GroupedTableHeader>

      {/* Level-2 header: workspace columns, shown once — replaces the repeated blue bands. */}
      <GroupedTableChildHeader grid="" style={{ gridTemplateColumns: wsBandTemplate }}>
        <span className="flex items-center gap-1.5">
          <ChildRowMarker />
          Workspace
        </span>
        {visibleWsCols.map((col) => {
          const description = workspaceColumnDescription(col.colId, labels);
          return (
            <span
              key={col.colId}
              title={description}
              className={description ? 'cursor-help underline decoration-dotted underline-offset-2' : undefined}
            >
              {workspaceColumnLabel(col.colId, labels)}
            </span>
          );
        })}
      </GroupedTableChildHeader>

      {bills.slice(0, visibleCount).map((bill) => {
        const records = [...(bill.billWorkspaceRecords ?? [])].sort((a, b) =>
          a.workspaceName.toLowerCase().localeCompare(b.workspaceName.toLowerCase())
        );
        const expanded = !collapsedBillIds.has(bill.id) && records.length > 0;

        return (
          <div key={bill.id} className="border-b border-border-light/60 dark:border-border-dark/50">
            {/* Bill row — the whole row toggles; inner links stop propagation. */}
            <div
              className={`grid gap-3.5 px-5 py-4 items-start text-sm transition-colors ${
                records.length > 0
                  ? 'cursor-pointer hover:bg-primary-50/60 dark:hover:bg-background-dark-tertiary/40'
                  : ''
              }`}
              style={{ gridTemplateColumns: billTemplate }}
              onClick={records.length > 0 ? () => onToggleBill(bill.id) : undefined}
            >
              <span className="min-w-0 flex items-start gap-2.5">
                {records.length > 0 ? (
                  <button
                    type="button"
                    aria-label={
                      expanded
                        ? `Hide workspace records for ${bill.billNumber}`
                        : `Show workspace records for ${bill.billNumber}`
                    }
                    aria-expanded={expanded}
                    onClick={(e) => {
                      e.stopPropagation();
                      onToggleBill(bill.id);
                    }}
                    className="mt-1 flex-none text-accent-400 hover:text-primary-800 dark:hover:text-primary-dark-400 transition-colors"
                  >
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform ${expanded ? '' : '-rotate-90'}`}
                    />
                  </button>
                ) : (
                  <span className="w-3.5 flex-none" aria-hidden="true" />
                )}
                <span className="min-w-0">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/legislation/${bill.id}`);
                    }}
                    className={`${getBillLinkClasses()} font-bold tabular-nums`}
                  >
                    {bill.billNumber}
                  </button>
                  <span className="block break-words text-text-secondary dark:text-text-dark-secondary">
                    {bill.title}
                  </span>
                </span>
              </span>
              {visibleBillCols.map((col) => (
                <span key={col.colId} className="min-w-0">
                  {billValueCell(bill, col.colId, billActions)}
                </span>
              ))}
            </div>

            {/* Workspace records: indented accent band, no repeated labels. */}
            {expanded && (
              <div
                className="border-t border-border-light/60 dark:border-border-dark/50 bg-background-tertiary/40 dark:bg-background-dark-tertiary/30 py-1.5 pr-5"
                style={{ paddingLeft: `${20 + WS_INDENT_PX}px` }}
              >
                {records.map((record) => (
                  <div
                    key={`${bill.id}::${record.workspaceId}`}
                    className="grid gap-3.5 items-center py-2 pl-3 border-l-2 border-primary-200 dark:border-primary-800"
                    style={{ gridTemplateColumns: wsRowTemplate }}
                  >
                    <WorkspaceName name={record.workspaceName} />
                    {visibleWsCols.map((col) => (
                      <span key={col.colId} className="min-w-0">
                        {workspaceValueCell(bill, record, col, onOpenNotes, editing)}
                      </span>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {hasMore && (
        <div ref={sentinelRef} className="px-5 py-4 text-center text-xs text-text-tertiary dark:text-text-dark-tertiary">
          Loading more bills… ({visibleCount} of {bills.length})
        </div>
      )}

      {bills.length === 0 && (
        <div className="px-5 py-10 text-center text-sm text-text-tertiary dark:text-text-dark-tertiary">
          No bills found
        </div>
      )}
      </div>
      </div>

      {/* Scroll chevrons — same affordance and styles as the flat grid */}
      <button
        className={`ag-scroll-chevron ag-scroll-chevron-left ${canScrollLeft ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
        style={{ left: 0 }}
        onMouseDown={() => startScrolling('left')}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling('left')}
        onTouchEnd={stopScrolling}
        aria-label="Scroll table left"
        tabIndex={canScrollLeft ? 0 : -1}
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        className={`ag-scroll-chevron ag-scroll-chevron-right ${canScrollRight ? 'ag-scroll-chevron-visible' : 'ag-scroll-chevron-hidden'}`}
        onMouseDown={() => startScrolling('right')}
        onMouseUp={stopScrolling}
        onMouseLeave={stopScrolling}
        onTouchStart={() => startScrolling('right')}
        onTouchEnd={stopScrolling}
        aria-label="Scroll table right"
        tabIndex={canScrollRight ? 0 : -1}
      >
        <ChevronRight className="w-5 h-5" />
      </button>
      </div>
    </GroupedTableContainer>
  );
};

export default NestedBillTable;
